import { Modal, Stack } from "@mantine/core";
import { useEffect } from "react";
import { useFeedbackForm } from "@/features/contact/hooks/useFeedbackForm";
import { FeedbackFields } from "@/features/contact/components/FeedbackFields";
import { FormLayout } from "@/components/forms/layouts/FormLayout";

interface FeedbackModalProps {
  opened: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

/**
 * Modal for collecting feedback when PostHog surveys are unavailable (cookies rejected).
 * Uses Convex to store feedback with email and message fields.
 */
export function FeedbackModal({ 
  opened, 
  onClose, 
  title = "Send Feedback",
  subtitle = "We value your feedback. Let us know how we can improve."
}: FeedbackModalProps) {
  const { form, handleSubmit, errorMessage, successMessage, isSubmitting } = useFeedbackForm();

  // Close modal on successful submission
  useEffect(() => {
    if (successMessage && opened) {
      // Close after a brief delay to let user see success message
      const timer = setTimeout(() => {
        onClose();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [successMessage, opened, onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="md"
      overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
    >
      <Stack gap="md" component="form" onSubmit={handleSubmit}>
        <FormLayout
          subtitle={subtitle}
          errorMessage={errorMessage}
          successMessage={successMessage}
          isSubmitting={isSubmitting}
        >
          <FeedbackFields form={form} />
          <form.AppForm>
            <form.SubscribeButton label="Submit Feedback" />
          </form.AppForm>
        </FormLayout>
      </Stack>
    </Modal>
  );
}
